import React, { useEffect, useMemo, useRef, useState } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import InputField from "../shared-components/InputField";
import { RecurCreate } from "../api/Person";
import { ReportsPendingReviewItem, ReportSummaryItemApi } from "../api/Reports";
import { useGetPersonAccounts, useGetPersonInfo } from "../api/PersonApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import { classNames } from "../utils";

type NextCheckRunMode = "run_now" | "schedule";

function startOfLocalDay(d: Date): Date {
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  return day;
}

/** Local calendar date for tomorrow as yyyy-MM-dd (HTML date input min). */
function tomorrowLocalISODate(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Display format: MMM d, yyyy (e.g. Aug 4, 2026). */
function formatLastCheckRanLabel(
  value: string | Date | null | undefined
): string {
  try {
    if (value === null || value === undefined) return "NA";
    const date: Date = typeof value === "string" ? parseISO(value) : value;
    if (isNaN(date.getTime())) return "NA";
    return format(date, "MMM d, yyyy");
  } catch {
    return "NA";
  }
}

function lastCheckRanAt(
  report: ReportSummaryItemApi | ReportsPendingReviewItem
): string | Date | null | undefined {
  if ("finish_at" in report && report.finish_at) {
    return report.finish_at;
  }
  if ("finished_at" in report && report.finished_at) {
    return report.finished_at;
  }
  return undefined;
}

type RecurModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RecurCreate) => void | Promise<void>;
  report: ReportSummaryItemApi | ReportsPendingReviewItem; // hackish, but has fields we need in either
};

const RecurModalForm = ({
  isOpen,
  onClose,
  onSave,
  report,
}: RecurModalProps) => {
  const [nextCheckRun, setNextCheckRun] =
    useState<NextCheckRunMode>("run_now");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [schedule, setSchedule] = useState<string>("");
  const [validated, setValidated] = useState<boolean>(false);
  const [doubleCheck, setDoubleCheck] = useState<boolean>(false);
  const [errorOnSummary, setErrorOnSummary] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Sync guard: React state alone cannot block same-tick double-clicks before re-render.
  const isSubmittingRef = useRef(false);
  const isSaveInFlightRef = useRef(false);

  // The submit lock is only released when the modal is opened again, so it
  // survives the closing transition instead of flickering back to enabled.
  // A save that is still running keeps the lock, because Escape or a backdrop
  // click can close the modal mid-request and the reopened form would otherwise
  // allow a second submit that creates a duplicate subscription.
  useEffect(() => {
    if (isOpen && !isSaveInFlightRef.current) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const minNextRunDate = useMemo(() => tomorrowLocalISODate(), []);

  const verifyRecurText =
    "By checking this box, you are signing up for an Employee Screening Subscription for this individual and agree to be billed for each screening at your contracted rates. Contact your Fama Account Manager for subscription prices.";
  const {
    data: personEx,
    isLoading: loadingPersonEx,
    mutate: getPersonEx,
  } = useGetPersonInfo();
  const {
    data: personAccounts,
    isLoading: loadingPersonAccounts,
    mutate: getPersonAccounts,
  } = useGetPersonAccounts();

  const lastRanLabel = formatLastCheckRanLabel(lastCheckRanAt(report));

  const handleValidate = () => {
    let temp: string[] = [];
    if (nextCheckRun === "schedule") {
      if (!startDate || isNaN(startDate.getTime())) {
        temp.push("Next Run Date is required");
      } else {
        const selectedDay = startOfLocalDay(startDate);
        const today = startOfLocalDay(new Date());
        if (selectedDay.getTime() <= today.getTime()) {
          temp.push("Next Run Date must be a future date");
        }
      }
    }
    const validFrequency: boolean =
      Number(schedule) > 0 ||
      (schedule.startsWith("cron(") && schedule.endsWith(")"));
    if (!validFrequency) {
      temp.push("Frequency is required");
    } else if (schedule.length > 255) {
      temp.push("Frequency must not exceed 255 characters");
    }
    if (temp.length === 0) {
      setValidated(true);
      if (!personEx && !loadingPersonEx) {
        getPersonEx(report.person_uuid);
      }
      if (!personAccounts && !loadingPersonAccounts) {
        getPersonAccounts(report.person_uuid);
      }
    }
    setErrors(temp);
  };

  const handleSubmit = async () => {
    if (!doubleCheck) {
      setErrorOnSummary(true);
      return;
    }
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    isSaveInFlightRef.current = true;
    setIsSubmitting(true);
    const runNow = nextCheckRun === "run_now";
    const data: RecurCreate = {
      person_uuid: report.person_uuid,
      company_uuid: report.company_uuid,
      report_uuid: report.report_uuid,
      schedule_value: schedule,
      run_now: runNow,
      ...(runNow || !startDate ? {} : { start_date: startDate }),
    };
    try {
      await onSave(data);
      // Stay disabled on success: `onSave` closes the modal, and the panel is
      // still visible during its leave transition.
    } catch {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    } finally {
      isSaveInFlightRef.current = false;
    }
  };

  if (!validated)
    return (
      <Modal open={isOpen} onClose={onClose} width="w-full max-w-lg p-3">
        <div>
          <div className="text-2xl font-bold border-b-2 mb-3 pb-3">
            Setup Employee Rescreening
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Next scheduled checks will capture new or updated data since your
            last check ran on {lastRanLabel}.
          </p>
          <div className="space-y-4">
            <div className="w-full relative">
              <label
                htmlFor="next-check-run"
                className="block text-sm font-medium text-gray-600 mb-2"
              >
                Next Check Run <span className="text-red-400">*</span>
              </label>
              <select
                id="next-check-run"
                name="next-check-run"
                value={nextCheckRun}
                onChange={(e) => {
                  const mode = e.target.value as NextCheckRunMode;
                  setNextCheckRun(mode);
                  if (mode === "run_now") {
                    setStartDate(null);
                  }
                }}
                className="w-full p-2.5 border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block"
              >
                <option value="run_now">Run Now</option>
                <option value="schedule">Schedule for Later Date</option>
              </select>
            </div>
            {nextCheckRun === "schedule" && (
              <div>
                <InputField
                  value={
                    startDate
                      ? startDate.toISOString().split("T")[0]
                      : ""
                  }
                  handleChange={(e) => {
                    if (!e.target.value) {
                      setStartDate(null);
                      return;
                    }
                    let picked = new Date(e.target.value);
                    if (!isNaN(picked.getTime())) {
                      const now = new Date();
                      const newHour = picked.getHours() + now.getUTCHours();
                      picked.setHours(
                        newHour,
                        now.getUTCMinutes(),
                        now.getUTCSeconds()
                      ); // handles rollover
                      setStartDate(picked);
                    }
                  }}
                  name={"next-run-date"}
                  placeholder="mm/dd/yyyy"
                  label={"Next Run Date *"}
                  type="date"
                  min={minNextRunDate}
                />
              </div>
            )}
            <div>
              <InputField
                name="schedule"
                label="Frequency *"
                placeholder="0"
                value={schedule}
                handleChange={(e) => setSchedule(e.target.value)}
                units="Months"
                classes="w-auto"
                className="w-16 text-center"
              />
            </div>
            {errors.length > 0 && (
              <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
                <ul>
                  {errors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-4 pt-6">
              <Button
                text="Cancel"
                colorType={ButtonColorTypes.cancel}
                onClick={onClose}
              />
              <Button
                text="Next"
                type={ButtonTypes.default}
                colorType={ButtonColorTypes.success}
                onClick={handleValidate}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  if (validated && loadingPersonEx)
    return (
      <Modal open={isOpen} onClose={onClose} width="w-full max-w-2xl p-3">
        <LoadingSpinner />
      </Modal>
    );
  return (
    <Modal open={isOpen} onClose={onClose} width="w-full max-w-4xl p-3">
      <div>
        <div className="text-2xl font-bold border-b-2 mb-3 pb-3">
          <DocumentTextIcon
            className={"bg-blue-50 text-blue-700 rounded-full p-3 w-12 inline"}
          ></DocumentTextIcon>
          <span className="font-semibold text-xl ml-2 text-gray-900">
            Subscription Summary
          </span>
        </div>
        <div className="border p-5 text-gray-90 rounded-lg">
          <div className="flex gap-6">
            <div className="flex flex-col gap-3 flex-1 min-w-0">
              <div className="flex flex-col mb-4 min-w-0">
                <span className="text-gray-700 truncate">
                  Screening Company &amp; Title
                </span>
                <b
                  className="font-semibold truncate"
                  title={report.company_name}
                >
                  {report.company_name}
                </b>
                <span
                  className="font-semibold truncate"
                  title={report.job_title}
                >
                  {report.job_title}
                </span>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Check Type</span>
                <b
                  className="font-semibold truncate"
                  title={report.blueprint_name}
                >
                  {report.blueprint_name}
                </b>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Behavior Kit</span>
                <span
                  className="font-semibold truncate"
                  title={report.behavior_kit_name || "General"}
                >
                  {report.behavior_kit_name || "General"}
                </span>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Name</span>
                <b className="font-semibold truncate" title={report.full_name}>
                  {report.full_name}
                </b>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Email</span>
                {personEx?.emails.map((email) => (
                  <span
                    className="font-semibold truncate"
                    key={email.address}
                    title={email.address}
                  >
                    {email.address}
                  </span>
                ))}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Phone</span>
                {personEx?.phones.map((phone) => (
                  <span
                    className="font-semibold truncate"
                    key={phone.raw_number}
                    title={phone.raw_number}
                  >
                    {phone.raw_number}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 flex-1 min-w-0">
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Birthday</span>
                <span
                  className="font-semibold truncate"
                  title={personEx?.birth_date || ""}
                >
                  {personEx?.birth_date}
                </span>
              </div>
              {!personAccounts ? (
                <div className="flex flex-col min-w-0">
                  <span className="text-gray-700 truncate">Social Media</span>
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="flex flex-col min-w-0">
                  {personAccounts.map((ac) => (
                    <span
                      className="font-semibold truncate"
                      key={ac.url}
                      title={ac.url}
                    >
                      {ac.url}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Address</span>
                {personEx?.addresses.map((addr) => (
                  <React.Fragment
                    key={addr.line1 || "" + addr.city + addr.state + addr.zip}
                  >
                    <span className="font-semibold truncate" title={addr.line1}>
                      {addr.line1}
                    </span>
                    <span
                      className="font-semibold truncate"
                      title={`${addr.city} ${addr.state} ${addr.zip}`}
                    >
                      {addr.city} {addr.state} {addr.zip}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Education</span>
                {personEx?.degrees.map((ed) => (
                  <span
                    className="font-semibold truncate"
                    key={ed.name + ed.educator}
                    title={`${ed.name}, ${ed.educator}`}
                  >
                    {ed.name}, {ed.educator}
                  </span>
                ))}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Employment</span>
                {personEx?.jobs.map((job) => (
                  <span
                    className="font-semibold truncate"
                    key={job.title + job.employer}
                    title={`${job.title}, ${job.employer}`}
                  >
                    {job.title}, {job.employer}
                  </span>
                ))}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-gray-700 truncate">Subscribed</span>
                <b className="font-semibold truncate">Employee Screening</b>
                <b className="font-semibold truncate">
                  {nextCheckRun === "run_now"
                    ? "Run Now"
                    : startDate
                      ? startDate.toISOString().split("T")[0]
                      : ""}
                </b>
                <b className="font-semibold truncate">
                  Frequency: {schedule} months
                </b>
              </div>
            </div>
          </div>
        </div>
        <div>
          <LabeledCheckbox
            name="verify-recur"
            label={verifyRecurText}
            defaultChecked={doubleCheck}
            alignCheckTop={true}
            preserveLabelCasing={true}
            classes={classNames(
              "bg-white border-2 rounded-lg m-1 mt-5 p-2",
              errorOnSummary ? "border-red-500" : "border-our-blue"
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDoubleCheck(e.target.checked);
              if (e.target.checked) {
                setErrorOnSummary(false);
              }
            }}
          ></LabeledCheckbox>
          {errorOnSummary && (
            <div className="text-red-500">Please select check box</div>
          )}
        </div>
        <div className="flex justify-end gap-4 pt-6 items-center">
          <Button
            text="Back"
            colorType={ButtonColorTypes.cancel}
            onClick={() => setValidated(false)}
            disabled={isSubmitting}
          />
          {isSubmitting && <LoadingSpinner />}
          <Button
            text="Next"
            type={ButtonTypes.default}
            colorType={ButtonColorTypes.success}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RecurModalForm;
